import React, { useCallback } from "react";
import { Checkbox, createClasses, IconButton } from "@bloomberg/media-ui-core";
import { type TickerChartDataPoint } from "@bloomberg/media-ui-markets";
import { ExitThin } from "@bloomberg/media-ui-icons";
import styles from "./style.module.css";
import type { TickerChartLegendData } from "~types/types";
import { formatPriceLabel } from "~utils/portfolio/formatNumber";

export type TickerChartLegendProps = {
  className?: string;
  hoveredDataPoint: TickerChartDataPoint | undefined;
  tickerLegendData: TickerChartLegendData[];
  handleTickerSelectionChange?: (tickerId: string, isSelected: boolean) => void;
  handleTickerRemoval?: (ticker: string) => void;
  enableTickerRemoval?: boolean;
  applyInteractiveStyles?: boolean;
  allowFirstTickerRemoval?: boolean;
};

function InteractiveTickerPill(props: {
  tickerValue: number;
  tickerLegend: TickerChartLegendData;
  valuePostfix: string;
  enableTickerRemoval: boolean;
  handleTickerSelectionChange?: (tickerId: string, isSelected: boolean) => void;
  handleTickerRemoval?: (ticker: string) => void;
}) {
  const {
    tickerLegend,
    tickerValue,
    valuePostfix,
    enableTickerRemoval,
    handleTickerRemoval,
    handleTickerSelectionChange,
  } = props;

  // handle checkbox change event
  const handleTickerSelection = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      handleTickerSelectionChange?.(tickerLegend.id, ev.currentTarget.checked);
    },

    [handleTickerSelectionChange, tickerLegend.id]
  );

  // handle ticker remove event
  const handleTickerDismiss = useCallback(() => {
    handleTickerRemoval?.(tickerLegend.id);
  }, [handleTickerRemoval, tickerLegend.id]);

  return (
    <div className={styles.interactiveTickerPill} title={tickerLegend.name}>
      <Checkbox
        name={String(tickerLegend.id)}
        className={styles.tickerPillCheckbox}
        label={tickerLegend.name}
        onChange={handleTickerSelection}
        checked={tickerLegend.checked}
        disabled={tickerLegend.disabled}
        inputProps={{
          style: {
            backgroundColor: tickerLegend.color,
            borderColor: tickerLegend.color,
          },
        }}
      />

      <div className={styles.changeWrapper}>
        <span className={styles.changeValue}>
          {formatPriceLabel(tickerValue)}
          {valuePostfix}
        </span>

        {/* Enable dismiss button only if event handler is available */}
        {enableTickerRemoval && (
          <IconButton
            type="button"
            size="tiny"
            onClick={handleTickerDismiss}
            disabled={!handleTickerRemoval}
          >
            <ExitThin title={handleTickerRemoval ? "Remove" : ""} />
          </IconButton>
        )}
      </div>
    </div>
  );
}

export default function TickerChartLegend(props: TickerChartLegendProps) {
  const {
    className,
    tickerLegendData,
    hoveredDataPoint: dataPoint,
    handleTickerSelectionChange,
    handleTickerRemoval,
    enableTickerRemoval = false,
    allowFirstTickerRemoval = false,
    applyInteractiveStyles = false,
  } = props;

  // show percentage value, only when multiple tickers are selected
  const valuePostfix =
    tickerLegendData.filter((tickerLegend) => tickerLegend.checked).length > 1
      ? "%"
      : "";

  const isRemovalEnabledForTicker = (index: number): boolean => {
    if (tickerLegendData.length <= 1) return false;

    return allowFirstTickerRemoval || index !== 0;
  };

  return (
    <div
      className={createClasses([
        className,
        styles.tickerChartLegendContainer,
        { [styles.interactive]: applyInteractiveStyles },
      ])}
    >
      {tickerLegendData?.map((tickerLegend, index) => (
        <InteractiveTickerPill
          key={tickerLegend.id}
          tickerLegend={tickerLegend}
          tickerValue={(dataPoint?.[tickerLegend.id] as number) || 0}
          valuePostfix={valuePostfix}
          enableTickerRemoval={enableTickerRemoval}
          handleTickerSelectionChange={handleTickerSelectionChange}
          handleTickerRemoval={
            isRemovalEnabledForTicker(index) ? handleTickerRemoval : undefined
          }
        />
      ))}
    </div>
  );
}
